import * as queries from './queries';
import app from '../../app';
import { initialState } from './state';
import {hasPreviousPrescription, startRecommendationNextWeek} from "./getters";

export async function init({ state, dispatch }) {
  state.history.selectedPrescriptionId = '';
  await Promise.all([
    dispatch('getPrescriptionRules'),
    dispatch('getPreviousPrescription'),
    dispatch('getDefaultRules'),
  ]);

  await dispatch('runExpertToolAlgorithm');
}

export async function getPrescriptionRules({ dispatch, commit }) {
  try {
    const { data, errors } = await app.apolloClient.query({
      query: queries.getPrescriptionRules,
    });

    if (data && !errors) {
      const { expertToolPrescriptionRules } = data;

      if (expertToolPrescriptionRules && expertToolPrescriptionRules.length) {

        // add ">" for session duration single number values
        const updatedExpertToolPrescriptionRules = expertToolPrescriptionRules.map(item => {
          const singleNumberRegex = /^\d+$/;

          if (singleNumberRegex.test(item.sessionDurationLabel)) {
            item.sessionDurationLabel = `>${item.sessionDurationLabel}`
          }
          return item
        })

        commit('setPrescriptionRules', updatedExpertToolPrescriptionRules);
      }
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
  }
}

export async function getPreviousPrescription({ dispatch, commit, rootGetters }) {
  const patientId = rootGetters['patientSummary/id'];

  try {
    const { data, errors } = await app.apolloClient.query({
      query: queries.getPreviousPrescription,
      variables: { patientId },
    });

    if (data && !errors) {
      const { patientPrescriptions } = data;

      if (patientPrescriptions && patientPrescriptions.length) {
        commit('setPreviousRecommendation', patientPrescriptions[0]);

        // set selectedRules from previous prescription
        const previousRecommendationSelectedRules = patientPrescriptions[0]?.selectedRules.map(i => i.key);
        commit('updateSelectedRules', previousRecommendationSelectedRules);
      }
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
  }
}

export async function getDefaultRules({ dispatch, commit, rootGetters }) {
  const patientId = parseInt(rootGetters['patientSummary/id']);

  try {
    const { data, errors } = await app.apolloClient.query({
      query: queries.getDefaultRules,
      variables: { patientId },
    });

    if (!errors) {
      const defaultPatientPrescriptionRules = data.defaultPatientPrescriptionRules || [];
      const defaultRules = defaultPatientPrescriptionRules.map((item) => item.key);

      commit('setDefaultRules', defaultRules);
      commit('addToSelectedRules', defaultRules);
    } else {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
  }
}

export async function runExpertToolAlgorithm(
    { dispatch, commit, getters, rootGetters },
    { alwaysRun = false } = {}
) {
  const {
    selectedRules,
    hrAtrialFibrilationProvoked,
    hrMyocardialIschemicTreshold,
    hrVentricularTachycardia,
    hrICDTreshold,
    getAdditionalTrainingStrategiesPlusDetails
  } = getters;

  if (!selectedRules.length) {
    commit('setRecommendation', {});
    commit('toggleRecommendationSaved', false);
    commit('setRecommendationUpdatedByAlgorithm', true);
    return;
  }

  try {
    const { data, errors } = await app.apolloClient.query({
      query: queries.runExpertToolAlgorithm,
      variables: {
        patientId: parseInt(rootGetters['patientSummary/id']),
        alwaysRun,
        selectedRules,
        ...(hrAtrialFibrilationProvoked && {
          hrAtrialFibrilationProvoked: parseInt(hrAtrialFibrilationProvoked),
        }),
        ...(hrMyocardialIschemicTreshold && {
          hrMyocardialIschemicTreshold: parseInt(hrMyocardialIschemicTreshold),
        }),
        ...(hrVentricularTachycardia && {
          hrVentricularTachycardia: parseInt(hrVentricularTachycardia),
        }),
        ...(hrICDTreshold && {
          hrICDTreshold: parseInt(hrICDTreshold),
        }),
      },
    });

    if (data && !errors) {
      const { runExpertToolAlgorithm } = data;

      if (runExpertToolAlgorithm && runExpertToolAlgorithm.id) {
         // update additionalTrainingStartegies with rest of details
         const updatedPrescription = {
          ...runExpertToolAlgorithm,
          additionalTrainingStrategies: getAdditionalTrainingStrategiesPlusDetails(runExpertToolAlgorithm)
        }

        commit('setRecommendation', updatedPrescription);
        commit('toggleRecommendationSaved', false);
        commit('setRecommendationUpdatedByAlgorithm', true);
        dispatch('checkDefaultStartRecommendationNextWeek');
      }
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
  }
}

export function resetRecommendation({ commit }) {
  commit('setRecommendation', initialState.recommendation);
}

export function revertRecommendation({ dispatch, commit, getters }) {
  const recommendation = getters.vanillaRecommendation;
  if (recommendation) {
    commit('setRecommendation', recommendation);
    commit('setRecommendationReverted', true);
  } else {
    dispatch('resetRecommendation'); // Shouldn't happen
  }
}

export async function createPrescription({
  dispatch, commit, getters, rootGetters
}, { isNew = true, startRecommendationNextWeek = true}) {
  const {
    selectedRules,
    hrMyocardialIschemicTreshold,
    hrVentricularTachycardia,
    hrICDTreshold,
    recommendation,
  } = getters;

  if (!selectedRules.length) return;

  const { intensities } = recommendation;
  const intensity = intensities && intensities.length
    ? intensities[0] : { min: null, max: null, flags: [] };

  if (!recommendation.strengthTrainingFlags.length) return;

  try {
    // console.log('R', recommendation.heartRate);
    const { data, errors } = await app.apolloClient.query({
      query: queries.createUpdatePrescription,
      variables: {
        patientId: parseInt(rootGetters['patientSummary/id']),
        selectedRules,
        ...(hrMyocardialIschemicTreshold && {
          hrMyocardialIschemicTreshold: parseInt(hrMyocardialIschemicTreshold),
        }),
        ...(hrVentricularTachycardia && {
          hrVentricularTachycardia: parseInt(hrVentricularTachycardia),
        }),
        ...(hrICDTreshold && {
          hrICDTreshold: parseInt(hrICDTreshold),
        }),
        new: isNew,
        startNextWeek: startRecommendationNextWeek,
        ...(intensity.min && { minIntensity: intensity.min }),
        ...(intensity.max && { maxIntensity: intensity.max }),
        ...(intensity.flags.length && { intensityFlags: intensity.flags }),
        intensityIncludesHIT: recommendation.hasHIT,
        ...(recommendation.minFrequency && {
          minFrequency: recommendation.minFrequency,
        }),
        ...(recommendation.maxFrequency && {
          maxFrequency: recommendation.maxFrequency,
        }),
        ...(recommendation.frequencyFlags.length && {
          frequencyFlags: recommendation.frequencyFlags,
        }),
        ...(recommendation.minSessionDuration && {
          minSessionDuration: recommendation.minSessionDuration,
        }),
        ...(recommendation.maxSessionDuration && {
          maxSessionDuration: recommendation.maxSessionDuration,
        }),
        ...(recommendation.sessionDurationFlags.length && {
          sessionDurationFlags: recommendation.sessionDurationFlags,
        }),
        ...(recommendation.minProgramDuration && {
          minProgramDuration: recommendation.minProgramDuration,
        }),
        ...(recommendation.maxProgramDuration && {
          maxProgramDuration: recommendation.maxProgramDuration,
        }),
        ...(recommendation.programDurationFlags.length && {
          programDurationFlags: recommendation.programDurationFlags,
        }),
        strengthTrainingFlags: recommendation.strengthTrainingFlags,
        ...(recommendation.additionalTrainingStrategies && {
          additionalTraining: recommendation.additionalTrainingStrategies
        }),
        ...(recommendation.changeReason && {
          changeReason: recommendation.changeReason,
        }),
        ...(recommendation.heartRate && {
          heartRate: parseInt(recommendation.heartRate),
        }),
        ...(recommendation.heartRateCooldown && {
          heartRateCooldown: parseInt(recommendation.heartRateCooldown),
        }),
      },
    });

    if (data && !errors) {
      const { createUpdateExpertToolPrescription } = data;

      if (createUpdateExpertToolPrescription && createUpdateExpertToolPrescription.id) {
        commit('setRecommendation', createUpdateExpertToolPrescription);
        commit('setPreviousRecommendation', createUpdateExpertToolPrescription);
        commit('toggleRecommendationSaved', true);
        app.eventBus.$emit('afterExpertToolRecommendationSaved', { recommendation: createUpdateExpertToolPrescription });
        return true;
      } else {
        return false;
      }
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
      return false;
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
    return false;
  }
}

export async function getPrescriptionHistory({ dispatch, commit, rootGetters }) {
  const patientId = rootGetters['patientSummary/id'];

  try {
    const { data, errors } = await app.apolloClient.query({
      query: queries.getPatientPrescriptions,
      variables: { patientId },
    });

    if (data && !errors) {
      const { patientPrescriptions } = data;

      if (patientPrescriptions && patientPrescriptions.length) {
        commit('setHistoryPrescriptions', patientPrescriptions);
      }
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
  }
}

export async function getSelectedPrescriptionPatientSummary({
  dispatch, commit, rootGetters, getters,
}) {
  const patientId = rootGetters['patientSummary/id'];
  const atDate = getters.selectedPrescription.createdAt;

  if (!atDate) return;

  try {
    const { data, errors } = await app.apolloClient.query({
      query: queries.getPatientSummaryAtDate,
      variables: { patientId,  atDate },
    });

    if (data && !errors && data.patientSummary) {
      commit('setSelectedPrescriptionPatientSummary', data.patientSummary);
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
  }
}

export function updateSelectedRules({ dispatch, commit }, selectedRules) {
  commit('updateSelectedRules', selectedRules);
  dispatch('runExpertToolAlgorithm', {alwaysRun : true});
}

export function addToSelectedRules({ dispatch, commit }, selectedRules) {
  commit('addToSelectedRules', selectedRules);
  dispatch('runExpertToolAlgorithm', {alwaysRun : true});
}

export function updateHrAtrialFibrilationProvoked({ dispatch, commit }, hrAtrialFibrilationProvoked) {
  commit('updateHrAtrialFibrilationProvoked', hrAtrialFibrilationProvoked);
  dispatch('runExpertToolAlgorithm', {alwaysRun : true});
}

export function updateHrMyocardialIschemicTreshold({ dispatch, commit }, hrMyocardialIschemicTreshold) {
  commit('updateHrMyocardialIschemicTreshold', hrMyocardialIschemicTreshold);
  dispatch('runExpertToolAlgorithm', {alwaysRun : true});
}

export function updateHrVentricularTachycardia({ dispatch, commit }, hrVentricularTachycardia) {
  commit('updateHrVentricularTachycardia', hrVentricularTachycardia);
  dispatch('runExpertToolAlgorithm', {alwaysRun : true});
}

export function updateHrICDTreshold({ dispatch, commit }, hrICDTreshold) {
  commit('updateHrICDTreshold', hrICDTreshold);
  dispatch('runExpertToolAlgorithm', {alwaysRun : true});
}

export function addRecommendationIntensity({ commit }) {
  commit('addRecommendationIntensity');
  commit('setRecommendationUpdatedByAlgorithm', false);
  commit('toggleRecommendationSaved', false);
}

export function removeRecommendationIntensity({ commit }, index) {
  commit('removeRecommendationIntensity', index);
  commit('setRecommendationUpdatedByAlgorithm', false);
  commit('toggleRecommendationSaved', false);
}

export function updateRecommendationIntensity({ commit }, payload) {
  commit('updateRecommendationIntensity', payload);
  commit('setRecommendationUpdatedByAlgorithm', false);
  commit('toggleRecommendationSaved', false);
}

export function setRecommendationHeartRate({ commit }, heartRate) {
  commit('setRecommendationHeartRate', heartRate);
  commit('setRecommendationUpdatedByAlgorithm', false);
  commit('toggleRecommendationSaved', false);
}

export function setRecommendationHeartRateCooldown({ commit }, heartRateCooldown) {
  commit('setRecommendationHeartRateCooldown', heartRateCooldown);
  commit('setRecommendationUpdatedByAlgorithm', false);
  commit('toggleRecommendationSaved', false);
}

export function setRecommendationMinFrequency({ commit }, minFrequency) {
  commit('setRecommendationMinFrequency', minFrequency);
  commit('setRecommendationUpdatedByAlgorithm', false);
  commit('toggleRecommendationSaved', false);
}

export function setRecommendationMaxFrequency({ commit }, maxFrequency) {
  commit('setRecommendationMaxFrequency', maxFrequency);
  commit('setRecommendationUpdatedByAlgorithm', false);
  commit('toggleRecommendationSaved', false);
}

export function setRecommendationFrequencyFlags({ commit }, frequencyFlags) {
  commit('setRecommendationFrequencyFlags', frequencyFlags);
  commit('setRecommendationUpdatedByAlgorithm', false);
  commit('toggleRecommendationSaved', false);
}

export function setRecommendationMinSessionDuration({ commit }, minSessionDuration) {
  commit('setRecommendationMinSessionDuration', minSessionDuration);
  commit('setRecommendationUpdatedByAlgorithm', false);
  commit('toggleRecommendationSaved', false);
}

export function setRecommendationChangeReason({ commit }, recommendationChangeReason) {
  commit('setRecommendationChangeReason', recommendationChangeReason);
  commit('setRecommendationUpdatedByAlgorithm', false);
  commit('toggleRecommendationSaved', false);
}

export function setRecommendationMaxSessionDuration({ commit }, maxSessionDuration) {
  commit('setRecommendationMaxSessionDuration', maxSessionDuration);
  commit('toggleRecommendationSaved', false);
}

export function setRecommendationSessionDurationFlags({ commit }, sessionDurationFlags) {
  commit('setRecommendationSessionDurationFlags', sessionDurationFlags);
  commit('setRecommendationUpdatedByAlgorithm', false);
  commit('toggleRecommendationSaved', false);
}

export function setRecommendationMinProgramDuration({ commit }, minProgramDuration) {
  commit('setRecommendationMinProgramDuration', minProgramDuration);
  commit('setRecommendationUpdatedByAlgorithm', false);
  commit('toggleRecommendationSaved', false);
}

export function setRecommendationMaxProgramDuration({ commit }, maxProgramDuration) {
  commit('setRecommendationMaxProgramDuration', maxProgramDuration);
  commit('setRecommendationUpdatedByAlgorithm', false);
  commit('toggleRecommendationSaved', false);
}

export function setRecommendationProgramDurationFlags({ commit }, programDurationFlags) {
  commit('setRecommendationProgramDurationFlags', programDurationFlags);
  commit('setRecommendationUpdatedByAlgorithm', false);
  commit('toggleRecommendationSaved', false);
}

export function setRecommendationStrengthTrainingFlags({ commit }, strengthTrainingFlags) {
  commit('setRecommendationStrengthTrainingFlags', strengthTrainingFlags);
  commit('setRecommendationUpdatedByAlgorithm', false);
  commit('toggleRecommendationSaved', false);
}

export function setAdditionalTrainingStrategies({ commit }, additionalTrainingStrategies) {
  commit('setAdditionalTrainingStrategies', additionalTrainingStrategies);
  commit('setRecommendationUpdatedByAlgorithm', false);
  commit('toggleRecommendationSaved', false);
}

export function toggleRecommendationHITIntensity({ commit }, hasHIT) {
  commit('toggleRecommendationHITIntensity', hasHIT);
  commit('setRecommendationUpdatedByAlgorithm', false);
  commit('toggleRecommendationSaved', false);
}

export function setHistorySelectedPrescriptionId({ dispatch, commit }, selectedPrescriptionId) {
  commit('setHistorySelectedPrescriptionId', selectedPrescriptionId);

  if (selectedPrescriptionId) {
    dispatch('getSelectedPrescriptionPatientSummary');
  } else {
    commit('setSelectedPrescriptionPatientSummary', {});
  }
}

/**
 * Set the default value whether to start prescription right away, or next week.
 * COR001-6010
 * @param commit
 * @param getters
 * @param rootGetters
 */
export function checkDefaultStartRecommendationNextWeek({ commit, getters, rootGetters }) {
  if (
      !getters.hasPreviousRecommendation ||
      parseInt(rootGetters['patientSummary/getCurrentInProgressSDMEncounter']?.version) === 2
  ) {
    return commit('setStartRecommendationNextWeek', false);
  }

  return commit('setStartRecommendationNextWeek', true);
}

export async function reset({ commit }) {
  commit('reset');
}